/**
 * telemetry.ts -- Finance app Application Insights telemetry.
 *
 * Schema: OTel-aligned (service.name, service.namespace, trace.id, span.id, etc.)
 * consistent with apps/web and apps/blog telemetry modules.
 *
 * HC-LOGGING: emits to the shared core-loop-ai-prod workspace.
 * HC-NO-PII:  never logs user email, name, or raw auth tokens.
 *             user identity is attached as user.id (opaque userId) only after sign-in
 *             via the exported setTelemetryUser() function.
 *
 * Connection strings from .env.local (dev) / VITE_APPINSIGHTS_CONNECTION_STRING_PROD (prod).
 */
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

const SEVERITY_LEVELS = { INFO: 0, WARNING: 1, ERROR: 2, CRITICAL: 3 };

function randomHex(length: number): string {
  let out = "";
  while (out.length < length) {
    out += Math.random().toString(16).slice(2);
  }
  return out.slice(0, length);
}
function generateTraceId() { return randomHex(32); }
function generateSpanId() { return randomHex(16); }
function createTraceparent(traceId: string, spanId: string) {
  return `00-${traceId}-${spanId}-01`;
}

function hashBuildVersion(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function getBuildVersion() {
  return String(import.meta.env.VITE_BUILD_VERSION || import.meta.env.VITE_COMMIT_SHA || "dev");
}

function getSessionId(): string {
  const key = "core-loop.session-id";
  try {
    return window.sessionStorage.getItem(key) || (() => {
      const id = generateTraceId();
      window.sessionStorage.setItem(key, id);
      return id;
    })();
  } catch { return generateTraceId(); }
}

function getEnvironment() {
  return LOCAL_HOSTNAMES.has(window.location.hostname.toLowerCase()) ? "dev" : "prod";
}

function getSamplingPercentage() {
  const raw = Number(import.meta.env.VITE_APPINSIGHTS_SAMPLING_PERCENTAGE ?? 100);
  return isNaN(raw) ? 100 : Math.max(1, Math.min(100, raw));
}

function getConnectionString() {
  const dev = (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING_DEV || "").trim();
  const prod = (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING_PROD || "").trim();
  return LOCAL_HOSTNAMES.has(window.location.hostname.toLowerCase()) ? dev : prod;
}

function getSeverityLevel(statusCode: number) {
  if (statusCode >= 500) return SEVERITY_LEVELS.CRITICAL;
  if (statusCode >= 400) return SEVERITY_LEVELS.ERROR;
  if (statusCode >= 300) return SEVERITY_LEVELS.WARNING;
  return SEVERITY_LEVELS.INFO;
}

function getErrorMeta(errLike: unknown) {
  if (errLike instanceof Error) {
    return { type: errLike.name || "Error", message: (errLike.message || "unknown").slice(0, 512), stack: (errLike.stack || "").slice(0, 4096) };
  }
  return { type: "Error", message: String(errLike || "unknown").slice(0, 512), stack: "" };
}

// ─── Module-level user context (set after auth) ──────────────────────────────
let _userId: string | null = null;

/**
 * Call after successful sign-in. userId is the opaque sub/OID -- never email.
 * HC-NO-PII: do not pass email, name, or any identifiable string.
 */
export function setTelemetryUser(userId: string | null) {
  _userId = userId;
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function initTelemetry(): () => void {
  if (typeof window === "undefined" || import.meta.env.MODE === "test") return () => {};

  const connectionString = getConnectionString();
  if (!connectionString) return () => {};

  const appInsights = new ApplicationInsights({
    config: {
      connectionString,
      disableCookiesUsage: true,
      isCookieUseDisabled: true,
      enableSessionStorageBuffer: false,
      disableAjaxTracking: true,
      disableFetchTracking: true,
      disableExceptionTracking: false,
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: true,
      disableCorrelationHeaders: false,
      samplingPercentage: getSamplingPercentage(),
    },
  });

  appInsights.loadAppInsights();

  const buildVersion = getBuildVersion();
  const buildVersionHash = hashBuildVersion(buildVersion);
  const sessionId = getSessionId();
  const environment = getEnvironment();

  const traceContext: { traceId: string; activeSpanId: string | null } = {
    traceId: generateTraceId(),
    activeSpanId: null,
  };

  function baseDimensions(span?: Record<string, unknown>) {
    return {
      "service.name": "core-loop-finance",
      "service.namespace": "core-loop",
      "service.version": buildVersion,
      "service.version_hash": buildVersionHash,
      "deployment.environment": environment,
      "session.id": sessionId,
      "http.route": window.location.pathname,
      "url.full": window.location.href,
      // HC-NO-PII: userId is opaque sub/OID, not email
      "user.id": _userId ?? "anon",
      "trace.id": (span?.traceId as string) || traceContext.traceId,
      "span.id": (span?.spanId as string) || "",
      "span.parent_id": (span?.parentSpanId as string) || "",
      "span.kind": (span?.kind as string) || "internal",
    };
  }

  appInsights.addTelemetryInitializer((envelope) => {
    if (!envelope?.data?.baseData) return;
    const props = (envelope.data.baseData.properties ??= {});
    const defaults = baseDimensions();
    for (const [k, v] of Object.entries(defaults)) {
      if (!props[k]) props[k] = v;
    }
  });

  function startSpan(name: string, kind = "internal", parentSpanId: string | null = traceContext.activeSpanId, attributes: Record<string, unknown> = {}) {
    const span = { name, kind, traceId: traceContext.traceId, spanId: generateSpanId(), parentSpanId: parentSpanId || "", startTs: performance.now(), attributes };
    traceContext.activeSpanId = span.spanId;
    return span;
  }

  function endSpan(span: ReturnType<typeof startSpan>, status = "ok", extraProps: Record<string, unknown> = {}, extraMeasurements: Record<string, number> = {}) {
    const durationMs = Math.max(0, Math.round(performance.now() - span.startTs));
    appInsights.trackEvent(
      { name: span.name, measurements: { duration_ms: durationMs, ...extraMeasurements } },
      { ...baseDimensions(span), ...span.attributes, ...extraProps, "span.status": status, "telemetry.model": "opentelemetry-aligned" }
    );
    if (traceContext.activeSpanId === span.spanId) traceContext.activeSpanId = span.parentSpanId || null;
    return durationMs;
  }

  // Page load
  const trackPageLoad = () => {
    const span = startSpan("page.load", "internal", "", { "span.name": "page.load" });
    const t = window.performance?.timing;
    if (t) {
      const ns = t.navigationStart;
      appInsights.trackPageView({
        name: document.title || "page-view",
        uri: window.location.pathname,
        properties: { loadStatus: "success", ...baseDimensions(span) },
      });
      endSpan(span, "ok", { "event.type": "page.load", path: window.location.pathname, title: document.title || "unknown" }, {
        page_load_ms: t.loadEventEnd > 0 ? t.loadEventEnd - ns : Math.round(performance.now()),
        dom_ready_ms: t.domContentLoadedEventEnd > 0 ? t.domContentLoadedEventEnd - ns : 0,
      });
    } else {
      endSpan(span, "ok");
    }
  };
  if (document.readyState === "complete") trackPageLoad();
  else window.addEventListener("load", trackPageLoad, { once: true });

  // Uncaught errors
  const onError = (event: ErrorEvent) => {
    const span = startSpan("error.uncaught", "internal", traceContext.activeSpanId);
    const meta = getErrorMeta(event.error || event.message);
    endSpan(span, "error", {
      "exception.type": meta.type, "exception.message": meta.message,
      "exception.filename": event.filename || "", "exception.lineno": String(event.lineno || 0),
      severity: String(SEVERITY_LEVELS.ERROR), path: window.location.pathname,
    }, { error_stack_length: meta.stack.length });
  };
  window.addEventListener("error", onError);

  // Unhandled promise rejections
  const onRejection = (event: PromiseRejectionEvent) => {
    const span = startSpan("error.unhandled_rejection", "internal", traceContext.activeSpanId);
    const meta = getErrorMeta(event.reason);
    endSpan(span, "error", { "exception.type": meta.type, "exception.message": meta.message, severity: String(SEVERITY_LEVELS.ERROR), path: window.location.pathname });
  };
  window.addEventListener("unhandledrejection", onRejection);

  // Fetch instrumentation (LLM calls, API calls)
  const AI_RE = /\.(?:applicationinsights\.azure\.com|monitor\.azure\.com|services\.visualstudio\.com)(?:\/|$)/i;
  const originalFetch = window.fetch;
  if (originalFetch) {
    window.fetch = function (...args) {
      const input = args[0];
      const init = args[1] || {};
      const method = (init.method || (typeof input === "object" ? (input as Request).method : undefined) || "GET");
      const url = typeof input === "string" ? input : (input as Request).url || "unknown";
      if (AI_RE.test(url)) return originalFetch.apply(this, args);

      const span = startSpan("http.client", "client", traceContext.activeSpanId, { "http.method": method, "url.full": url });
      let finalArgs: typeof args = args;
      try {
        const abs = new URL(url, window.location.origin);
        if (abs.origin === window.location.origin) {
          const headers = new Headers(init.headers || {});
          headers.set("traceparent", createTraceparent(span.traceId, span.spanId));
          finalArgs = [input, { ...init, headers }];
        }
      } catch { /* malformed URL */ }

      return originalFetch.apply(this, finalArgs)
        .then(response => {
          endSpan(span, response.status >= 400 ? "error" : "ok", { "http.status_code": String(response.status), severity: String(getSeverityLevel(response.status)), path: window.location.pathname });
          return response;
        })
        .catch(error => {
          const meta = getErrorMeta(error);
          endSpan(span, "error", { "exception.type": meta.type, "exception.message": meta.message, severity: String(SEVERITY_LEVELS.ERROR), path: window.location.pathname });
          throw error;
        });
    };
  }

  // data-track click events
  const onClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target.closest("[data-track]") : null;
    const control = target?.getAttribute("data-track");
    if (!control) return;
    const span = startSpan("ui.click", "ui", traceContext.activeSpanId, { control, path: window.location.pathname });
    endSpan(span, "ok");
  };
  document.addEventListener("click", onClick, { capture: true, passive: true });

  return () => {
    document.removeEventListener("click", onClick, true);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    if (window.fetch !== originalFetch) window.fetch = originalFetch;
    appInsights.flush();
  };
}

export const __testing = { generateTraceId, generateSpanId, createTraceparent, hashBuildVersion };
